// TermsPrivacyBlockingModal.tsx
import React, { FC, useEffect, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import Loader from "@shared/components/Loader";
import { toastService } from "@services/ToastService";
import { userService } from "@services/UserServices";
import { useUser } from "@services/UserContext";
import { LegalDoc, LegalModal } from "./LegalModal";

/** Props:
 * - isOpen: control visibility (render nothing when false)
 * - onAccepted?: optional callback after successful acceptance
 */
interface TermsPrivacyBlockingModalProps {
  isOpen: boolean;
  onAccepted?: () => void;
}

type FormValues = {
  termsAccepted: boolean;
  privacyAccepted: boolean;
};

/**
 * Full-screen blocking modal that cannot be dismissed until both checkboxes are accepted.
 * Shows links to open Terms & Conditions and Privacy Policy in an iframe modal (LegalModal).
 */
const TermsPrivacyBlockingModal: FC<TermsPrivacyBlockingModalProps> = ({
  isOpen,
  onAccepted,
}) => {
  const { register, handleSubmit, formState, watch, setError, clearErrors } =
    useForm<FormValues>({
      mode: "onChange",
      defaultValues: { termsAccepted: false, privacyAccepted: false },
    });
  const { errors, isSubmitting } = formState;

  const { refresh, setRefresh } = useUser();

  const [legalOpen, setLegalOpen] = useState(false);
  const [legalDoc, setLegalDoc] = useState<LegalDoc | null>(null);

  const openLegal = useCallback((doc: LegalDoc) => {
    setLegalDoc(doc);
    setLegalOpen(true);
  }, []);
  const closeLegal = useCallback(() => {
    setLegalOpen(false);
    setLegalDoc(null);
  }, []);

  // Prevent background scroll while blocking modal is open
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // Focus lock on mount (basic)
  const modalRef = React.useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isOpen) modalRef.current?.focus();
  }, [isOpen]);

  const termsChecked = watch("termsAccepted");
  const privacyChecked = watch("privacyAccepted");
  const canAccept = termsChecked && privacyChecked && !isSubmitting;

  const onSubmit = async (values: FormValues) => {
    // Guard (react-hook-form should prevent submit without both, but keep it defensive)
    if (!values.termsAccepted || !values.privacyAccepted) {
      if (!values.termsAccepted) {
        setError("termsAccepted", {
          type: "manual",
          message: "Please accept the Terms & Conditions to continue.",
        });
      }
      if (!values.privacyAccepted) {
        setError("privacyAccepted", {
          type: "manual",
          message: "Please accept the Privacy Policy to continue.",
        });
      }
      return;
    }

    try {
      await userService.acceptTerms(); // you mentioned this exists
      toastService.success("Thanks! Your acceptance was recorded.");
      setRefresh(refresh + 1);
      onAccepted?.();
      // Intentionally no 'close' here — parent controls visibility (e.g., hide when accepted)
    } catch (err) {
      console.error("Failed to accept terms:", err);
      toastService.error("Unable to record acceptance. Please try again.");
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Blocking Modal */}
      <div
        className="fixed inset-0 z-[999] flex items-center justify-center"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tpb-title"
        // DO NOT close on overlay click — non-dismissible by design
      >
        {/* Overlay (no onClick) */}
        <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" />

        <div
          ref={modalRef}
          tabIndex={-1}
          className="relative w-full max-w-lg mx-4 rounded-xl bg-white shadow-2xl border border-gray-200 focus:outline-none"
          // Swallow clicks to avoid any bubbling side-effects
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="px-6 pt-6 pb-4">
            <div className="flex flex-col items-center text-center">
              <img src="/logo_new.svg" alt="The Ark" className="w-28 mb-4" />
              <h2 id="tpb-title" className="text-lg font-semibold mb-2">
                We’ve updated our Terms &amp; Conditions and Privacy Policy
              </h2>
              <p className="text-sm text-gray-600">
                To continue using The Ark, please review and accept the updated
                policies.
              </p>
            </div>

            {/* Checkboxes */}
            <form className="mt-6" onSubmit={handleSubmit(onSubmit)} noValidate>
              <div className="space-y-3 mb-5">
                <label className="flex items-start gap-2 text-sm text-gray-800">
                  <input
                    id="termsAccepted"
                    type="checkbox"
                    {...register("termsAccepted", {
                      required: true,
                      onChange: () => clearErrors("termsAccepted"),
                    })}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 focus:ring-2 focus:ring-blue-600"
                    aria-invalid={!!errors.termsAccepted}
                    aria-describedby={
                      errors.termsAccepted ? "termsAccepted-error" : undefined
                    }
                  />
                  <span>
                    I agree to the{" "}
                    <a
                      href="/terms"
                      onClick={(e) => {
                        e.preventDefault();
                        openLegal("terms");
                      }}
                      className="text-blue-600 underline"
                    >
                      Terms &amp; Conditions
                    </a>
                    .
                  </span>
                </label>
                {errors.termsAccepted && (
                  <p id="termsAccepted-error" className="text-xs text-red-600">
                    {errors.termsAccepted.message ??
                      "Please accept the Terms & Conditions to continue."}
                  </p>
                )}

                <label className="flex items-start gap-2 text-sm text-gray-800">
                  <input
                    id="privacyAccepted"
                    type="checkbox"
                    {...register("privacyAccepted", {
                      required: true,
                      onChange: () => clearErrors("privacyAccepted"),
                    })}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 focus:ring-2 focus:ring-blue-600"
                    aria-invalid={!!errors.privacyAccepted}
                    aria-describedby={
                      errors.privacyAccepted
                        ? "privacyAccepted-error"
                        : undefined
                    }
                  />
                  <span>
                    I agree to the{" "}
                    <a
                      href="/privacy"
                      onClick={(e) => {
                        e.preventDefault();
                        openLegal("privacy");
                      }}
                      className="text-blue-600 underline"
                    >
                      Privacy Policy
                    </a>
                    .
                  </span>
                </label>
                {errors.privacyAccepted && (
                  <p
                    id="privacyAccepted-error"
                    className="text-xs text-red-600"
                  >
                    {errors.privacyAccepted.message ??
                      "Please accept the Privacy Policy to continue."}
                  </p>
                )}
              </div>

              {/* Bottom actions */}
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-gray-500">
                  You must accept both to continue.
                </p>
                <button
                  type="submit"
                  className={`inline-flex items-center justify-center rounded-md px-4 py-2 text-white ${
                    canAccept
                      ? "bg-blue-700 hover:bg-blue-800"
                      : "bg-blue-400 cursor-not-allowed"
                  } transition`}
                  disabled={!canAccept || isSubmitting}
                  aria-disabled={!canAccept}
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <Loader color="#fff" size="sm" />
                    </span>
                  ) : (
                    "Accept"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Legal iframe modal (can be dismissed independently) */}
      <LegalModal isOpen={legalOpen} onClose={closeLegal} doc={legalDoc} />
    </>
  );
};

export default TermsPrivacyBlockingModal;
